/**
 * Admin "Assessment leads" panel — public lite self-assessment submissions.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "../../lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso || "—"; }
};

const SUB_COLOUR = {
  ITSM360: "bg-[#7737F6]",
  AI360: "bg-[#0B7DCB]",
  DATA360: "bg-[#1E9E5A]",
};

export default function AssessmentLeadsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/assessment-leads");
      setItems(data.items || []);
    } catch (err) {
      toast.error("Failed to load assessment leads");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const exportCsv = () => {
    const a = document.createElement("a");
    a.href = `${API_BASE}/admin/assessment-leads.csv`;
    a.click();
  };

  return (
    <div data-testid="admin-assessment-leads-panel">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Assessment leads</h2>
          <p className="text-zinc-500 text-[13px] mt-1">Public self-assessment submissions from <code className="text-[12px]">/resources/{`{slug}`}-self-assessment</code>. {items.length} total.</p>
        </div>
        <button type="button" onClick={exportCsv} className="ek-btn ek-btn-outline text-[12px]" data-testid="assessment-leads-csv"><Download size={13}/> Export CSV</button>
      </div>
      {loading ? (
        <p className="text-zinc-500 text-[13px]">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 text-center">
          <p className="text-zinc-600 text-[14px]">No public self-assessment submissions yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-zinc-200">
          <table className="min-w-full text-[13px]">
            <thead className="bg-zinc-50 text-left text-zinc-500 text-[11px] uppercase tracking-[0.12em]">
              <tr>
                <th className="p-3">Date</th>
                <th className="p-3">Sub-brand</th>
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Organisation</th>
                <th className="p-3">Sector</th>
                <th className="p-3">Score</th>
                <th className="p-3">Level</th>
                <th className="p-3">Top gaps</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`alead-${it.id}`}>
                  <td className="p-3 text-zinc-600 whitespace-nowrap">{fmtDate(it.created_at)}</td>
                  <td className="p-3"><span className={`text-white text-[10.5px] font-bold tracking-[0.14em] px-2 py-0.5 ${SUB_COLOUR[it.sub_brand] || "bg-zinc-700"}`}>{it.sub_brand}</span></td>
                  <td className="p-3 font-medium">{it.full_name}</td>
                  <td className="p-3"><a href={`mailto:${it.email}`} className="text-[#7737F6] hover:underline">{it.email}</a></td>
                  <td className="p-3 text-zinc-700">{it.organisation || "—"}</td>
                  <td className="p-3 text-zinc-600">{it.sector || "—"}</td>
                  <td className="p-3 tabular-nums">{it.result_overall ?? "—"}/4</td>
                  <td className="p-3">{it.result_level || "—"}</td>
                  <td className="p-3 text-[11.5px] text-zinc-600">{(it.top_three_gaps || []).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
